import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PersonnelTimeAllocationCalculator.css";

const PersonnelTimeAllocationCalculator = () => {
    // State variables for the calculator
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [timeAllocationData, setTimeAllocationData] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [allGrantNumbers, setAllGrantNumbers] = useState([]);

    // Add this helper function to correctly format the date string
    const formatDateString = (dateStr) => {
        // Parse the date elements from the ISO string format YYYY-MM-DD
        const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
        
        // Create a date object with numeric values to avoid timezone issues
        // Note: month is 0-indexed in JavaScript Date
        const date = new Date(year, month - 1, day);
        
        // Format the date to display in a user-friendly way
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Calculate time allocation between selected dates
    const calculateTimeAllocation = async () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end dates");
            return;
        }

        setIsCalculating(true);

        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            // Format dates for API
            const formattedStartDate = start.toISOString().split('T')[0];
            const formattedEndDate = end.toISOString().split('T')[0];
            
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/payroll/time-allocation`, {
                    params: {
                        startDate: formattedStartDate,
                        endDate: formattedEndDate
                    }
                }
            );

            console.log("Time allocation response:", response.data);

            processTimeAllocationData(response.data);
        } catch (error) {
            console.error("Error calculating time allocation:", error);
            alert("An error occurred while calculating time allocation");
        } finally {
            setIsCalculating(false);
        }
    };

    // Process the payroll data to calculate time allocation
    const processTimeAllocationData = (data) => {
        // Structure: {personName: {grantNumber: totalPercentage}}
        const personnelTimeMap = {};
        const grantNumbersSet = new Set();
        
        // First, calculate total payment for each person
        const personTotalPaymentMap = {};
        
        data.forEach(transaction => {
            const personName = transaction.Name;
            if (!personName) return;
            
            // Initialize person maps if they don't exist
            if (!personnelTimeMap[personName]) {
                personnelTimeMap[personName] = {};
            }
            if (!personTotalPaymentMap[personName]) {
                personTotalPaymentMap[personName] = 0;
            }
            
            // Add payment to total
            const payment = parseFloat(transaction.Payment) || 0;
            personTotalPaymentMap[personName] += payment;
            
            // Process default payment allocation
            if (transaction['Default Payment']) {
                let defaultPayment;
                
                try {
                    // Check if Default Payment is already an object
                    if (typeof transaction['Default Payment'] === 'object') {
                        defaultPayment = transaction['Default Payment'];
                    } else {
                        // Try to parse as JSON if it's a string
                        defaultPayment = JSON.parse(transaction['Default Payment']);
                    }
                    
                    // Calculate science time percentage
                    const scienceTime = 1; // Assuming 100% time is included for now
                    
                    // Iterate through each grant allocation
                    Object.entries(defaultPayment).forEach(([grantNumber, percentage]) => {
                        // Add grant number to set of all grants
                        grantNumbersSet.add(grantNumber);
                        
                        const grantPercentage = parseFloat(percentage) / 100;
                        const allocationValue = payment * scienceTime * grantPercentage;
                        
                        // Initialize grant allocation if it doesn't exist
                        if (!personnelTimeMap[personName][grantNumber]) {
                            personnelTimeMap[personName][grantNumber] = 0;
                        }
                        
                        // Add allocation
                        personnelTimeMap[personName][grantNumber] += allocationValue;
                    });
                } catch (error) {
                    console.error("Error processing default payment:", error);
                    console.log("Problematic value:", transaction['Default Payment']);
                    console.log("Type:", typeof transaction['Default Payment']);
                }
            }
        });
        
        // Convert the set to an array and sort
        const allGrantNumbersArray = Array.from(grantNumbersSet).sort();
        setAllGrantNumbers(allGrantNumbersArray);
        
        // Convert absolute values to percentages
        const results = [];

        console.log("Personnel Time Map:", personnelTimeMap);
        
        Object.entries(personnelTimeMap).forEach(([personName, grantAllocations]) => {
            const totalPayment = personTotalPaymentMap[personName];
            
            if (totalPayment > 0) {
                const personEntry = { 
                    personName, 
                    allocations: {}
                };
                
                // Initialize all grants with 0%
                allGrantNumbersArray.forEach(grantNumber => {
                    personEntry.allocations[grantNumber] = 0;
                });
                
                // Fill in the actual percentages
                Object.entries(grantAllocations).forEach(([grantNumber, amount]) => {
                    const percentOfTime = (amount / totalPayment) * 100;
                    personEntry.allocations[grantNumber] = percentOfTime;
                });
                
                results.push(personEntry);
            }
        });
        
        // Sort by person name
        results.sort((a, b) => a.personName.localeCompare(b.personName));
        
        setTimeAllocationData(results);
    };

    // Get CSS class based on percentage
    const getPercentageClass = (percentage) => {
        if (percentage >= 40) return "high-percentage";
        if (percentage >= 20) return "medium-percentage";
        return "";
    };

    // Download time allocation data as CSV
    const downloadTimeAllocationCSV = () => {
        if (timeAllocationData.length === 0) return;

        // Create header row with personnel in first column and all grants in subsequent columns
        const headerRow = ["Personnel", ...allGrantNumbers];
        
        // Create data rows
        const dataRows = timeAllocationData.map(person => {
            return [
                person.personName,
                ...allGrantNumbers.map(grantNumber => 
                    person.allocations[grantNumber]?.toFixed(2) || "0.00"
                )
            ];
        });
        
        const csvContent = [
            headerRow.join(","),
            ...dataRows.map(row => row.join(","))
        ].join("\n");
        
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `personnel_time_allocation_${startDate}_to_${endDate}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="time-allocation-section">
            <h3>Personnel Time Allocation Calculator</h3>
            <p>Calculate what percentage of time personnel spent on each grant between selected dates.</p>
            
            <div className="date-range-selector">
                <label>Start Date:</label>
                <input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                
                <label>End Date:</label>
                <input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />
                
                <button 
                    className="calculate-btn"
                    onClick={calculateTimeAllocation}
                    disabled={isCalculating || !startDate || !endDate}
                >
                    {isCalculating ? "Calculating..." : "Calculate"}
                </button>
            </div>
            
            {timeAllocationData.length > 0 && (
                <div className="time-allocation-results">
                    <div className="results-header">
                        <h4>Results for period: {formatDateString(startDate)} - {formatDateString(endDate)}</h4>
                        <button 
                            className="download-btn"
                            onClick={downloadTimeAllocationCSV}
                        >
                            Download CSV
                        </button>
                    </div>
                    
                    <div className="table-container">
                        <table className="time-allocation-table matrix-layout">
                            <thead>
                                <tr>
                                    <th className="personnel-header">Personnel</th>
                                    {allGrantNumbers.map(grantNumber => (
                                        <th key={grantNumber} className="grant-header">
                                            {grantNumber}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {timeAllocationData.map((person) => (
                                    <tr key={person.personName}>
                                        <td className="personnel-cell">{person.personName}</td>
                                        {allGrantNumbers.map(grantNumber => (
                                            <td 
                                                key={`${person.personName}-${grantNumber}`}
                                                className={`allocation-cell ${getPercentageClass(person.allocations[grantNumber] || 0)}`}
                                            >
                                                {person.allocations[grantNumber]?.toFixed(2) || "0.00"}%
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonnelTimeAllocationCalculator;